/**
 * LOUD/QUIET Pill — rounded status/label capsule with a 2px ink border and a small hard
 * shadow. Used for tags, statuses and eyebrow badges.
 *
 * Ported 1:1 from the compiled `_ds_bundle.js`. Renders a <span>, so it is safe inside running
 * text and inside a Button label.
 *
 * Non-obvious decision: `width: fit-content` is set explicitly so the capsule keeps its shape
 * when dropped into a flex/grid cell that would otherwise stretch it.
 */
import * as React from 'react';

export type PillColor = 'yellow' | 'pink' | 'blue' | 'mint' | 'purple' | 'paper';

export interface PillProps extends React.HTMLAttributes<HTMLSpanElement> {
  children?: React.ReactNode;
  /** Fill + text colour pairing. @default "yellow" */
  color?: PillColor;
  /** Hard 2px offset shadow. @default true */
  shadow?: boolean;
  style?: React.CSSProperties;
}

const PALETTE: Record<PillColor, { bg: string; fg: string }> = {
  yellow: { bg: 'var(--lq-yellow)', fg: 'var(--lq-ink)' },
  pink: { bg: 'var(--lq-pink)', fg: 'var(--lq-ink)' },
  blue: { bg: 'var(--lq-blue)', fg: 'var(--lq-white)' },
  mint: { bg: 'var(--lq-mint)', fg: 'var(--lq-ink)' },
  // Ink on Grape measures 5.11:1 (#111111 on #8b6cff), so the 11px label clears WCAG AA for
  // normal text (4.5:1). It is also the best of the palette's options — white would be 3.69:1
  // and paper 3.47:1, both failures. An earlier note here claimed this pairing was sub-AA;
  // that was wrong, and inverting the text on the strength of it would have broken contrast.
  purple: { bg: 'var(--lq-purple)', fg: 'var(--lq-ink)' },
  paper: { bg: 'var(--lq-paper)', fg: 'var(--lq-ink)' },
};

export function Pill({ children, color = 'yellow', shadow = true, style, ...rest }: PillProps) {
  const palette = PALETTE[color];

  return (
    <span
      {...rest}
      style={{
        display: 'inline-flex',
        width: 'fit-content',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'var(--lq-border)',
        borderRadius: 'var(--lq-radius-full)',
        padding: '7px 13px',
        // NOTE: the uppercase capsule label is Geist *Sans* 900, not Geist Mono, even though the
        // readme assigns uppercase tags/eyebrows to the mono face. Bundle wins.
        fontFamily: 'var(--lq-font-sans)',
        fontSize: 11,
        lineHeight: 1,
        fontWeight: 900,
        textTransform: 'uppercase',
        color: palette.fg,
        background: palette.bg,
        // NOTE: a 2px offset — one step *below* the smallest documented shadow stop
        // (--lq-shadow-s is 3px). There is no 2px token, so this stays a literal.
        boxShadow: shadow ? '2px 2px 0 var(--lq-ink)' : 'none',
        ...style,
      }}
    >
      {children}
    </span>
  );
}

export default Pill;
